import { getBearerToken } from "../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import type { SupportTicketDetailApiResponse } from "../supportTicketTypes";
import { parseSupportTicketApiError } from "../utils/parseSupportTicketApiError";

/**
 * Request body for POST /pharmacy/support-tickets/update.
 *
 * `status` and `is_archived` are both optional; callers send whichever fields
 * they intend to change (e.g. archive uses `is_archived: true` only).
 */
export interface UpdateSupportTicketParams {
  org_id: string;
  ticket_id: string;
  /** New lifecycle status label (e.g. "Open", "In Progress"). */
  status?: string;
  /** When true, archives the ticket; when false, restores it. */
  is_archived?: boolean;
}

/**
 * Updates mutable fields on a support ticket.
 *
 * @param params - Organization id, ticket id, and optional status / archive fields.
 * @returns Updated ticket detail from the API.
 */
export const updateSupportTicket = async (
  params: UpdateSupportTicketParams,
): Promise<SupportTicketDetailApiResponse> => {
  const body: Record<string, string | boolean> = {
    org_id: params.org_id,
    ticket_id: params.ticket_id,
  };
  if (params.status !== undefined) {
    body.status = params.status;
  }
  if (params.is_archived !== undefined) {
    body.is_archived = params.is_archived;
  }

  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.UPDATE_SUPPORT_TICKET, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(
        await parseSupportTicketApiError(response, "Failed to update ticket"),
      );
    }

    const result = (await response.json()) as SupportTicketDetailApiResponse;
    logDataToConsole("updateSupportTicket", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in updateSupportTicket");
    throw error;
  }
};
